/* eslint-disable react-hooks/exhaustive-deps */
import Button from "@lhd/components/Button";
import {
  useControlStatement,
  useFetchOneAppByTypeV2,
  useFetchRecordV2,
  useGetAuth,
  useGetSelectOptionByAppPartType,
  useUpdateRecordV2,
} from "@lhd/hooks";
import { AppRecordV2, AppTypes, IPermissionItem, MODE } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { Form, Spin } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { CompanyPolicyContext } from "../../../../routes/CompanyPolicyProtectedRoute";
import {
  AgreementsPDF,
  BusinessTripReportPDF,
  CommonPDF,
  EmploymentContractPDF,
  FinancialReportsPDF,
  LaborDocumentPDF,
  RegulationPDF,
} from "../../components/PDFs";
import { APP_TYPES } from "../../constants";
import {
  UploadStatusProvider,
  useUploadStatus,
} from "../../contexts/UploadStatusContext";
import LayoutApp from "../../LayoutApp";
import {
  buildTreeAppPartType,
  convertDataToDataForm,
  handleDataSubmitFinish,
} from "../../untils";
import ButtonDuplicate from "./ButtonDuplicate";
import ButtonSaveAsNew from "./ButtonSaveAsNew";
import ButtonToCreate from "./ButtonToCreate";
import ButtonToSave from "./ButtonToSave";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { BUTTON_HEIGHT, FONT_SIZE, VIEW_MODE_OPTIONS } from "@lhd/constants";
import { StylesButton } from "./styled";
import ButtonToSubmit from "./ButtonToSubmit";
import ButtonToCancel from "./ButtonToCancel";
interface IProps {
  isQuickViewMode?: boolean;
  appType: AppTypes;
  getInstanceForm?: (form: FormInstance<any>) => void;
  extendsButtonRight?:
    | React.ReactNode
    | ((
        record?: AppRecordV2,
        hasChanged?: boolean,
        options?: {
          isEditing?: boolean;
        },
      ) => React.ReactNode);
  permission?: IPermissionItem;
  idsSelected?: { recordId?: string; appId?: string };
  isQuickView?: boolean;
  layoutAppProps?: Record<string, any>;
  showPrintButton?: boolean;
  disabledInEditMode?: boolean;
  configShowBtnsOnHeader?: {
    saveAsNew?: boolean;
    btnSave?: boolean;
    btnSubmit?: boolean;
    btnCancel?: boolean;
    btnDuplicate?: boolean;
    btnToCreate?: boolean;
    btnPreview?: boolean;
    btnPrintPdf?: boolean;
  };
  companyId?: number;
  hasControlPageMode?: boolean;
  hasRecordPermission?: boolean;
  excludePdfFieldNames?: string[];
}
const EditPageInner = (props: IProps) => {
  const {
    appType,
    permission,
    showPrintButton,
    disabledInEditMode,
    isQuickView,
    hasControlPageMode,
    hasRecordPermission,
  } = props;
  const uploadStatus = useUploadStatus();

  const {
    appId: appIdParam = "",
    id: idParam = "",
    companyId: companyIdParam = "",
  } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  useEffect(() => {
    props.getInstanceForm?.(form);
  }, [form, props.getInstanceForm]);
  const { data: dataContext } = useContext(CompanyPolicyContext);
  const companyId = props.companyId || companyIdParam || getCompanyId();

  const { getCurrentWorker, isAdmin, isSuperAdmin, isUserResigned } =
    useGetAuth();
  const currentWorker = getCurrentWorker();

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;

  const appId = props?.idsSelected?.appId ?? appIdParam;
  const id = props?.idsSelected?.recordId ?? idParam;

  const pageModeParam = searchParams.get("pageMode");
  const canNotEdit = hasRecordPermission
    ? !dataContext?.policy_permissions?.record_read ||
      !dataContext?.policy_permissions?.record_edit
    : !permission?.edit;

  const { elementSegmentedControl, value: pageModeValue } = useControlStatement(
    {
      options: VIEW_MODE_OPTIONS,
      displaySize,
      defaultValue: canNotEdit ? MODE.VIEW : pageModeParam || MODE.VIEW,
      disabled: canNotEdit,
      forceValue: canNotEdit ? MODE.VIEW : undefined,
    },
  );

  const contentElementSegmentedControl =
    isQuickView || !hasControlPageMode ? null : elementSegmentedControl;

  const pageMode = hasControlPageMode ? pageModeValue : pageModeParam;

  // Nhiều component con (cell của TableSheet, route guard...) đọc pageMode trực
  // tiếp từ URL, nên khi đổi mode bằng segmented control phải đồng bộ lại URL.
  // Nếu không, trang mở với ?pageMode=view sẽ giữ nguyên trạng thái read-only.
  useEffect(() => {
    if (!hasControlPageMode || isQuickView) return;
    if (!pageModeValue || pageModeParam === pageModeValue) return;

    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("pageMode", pageModeValue);
        return newParams;
      },
      { replace: true },
    );
  }, [hasControlPageMode, isQuickView, pageModeValue, pageModeParam]);

  const isEditMode =
    pageMode === "edit" &&
    (hasRecordPermission
      ? !!dataContext?.policy_permissions?.record_edit
      : !!permission?.edit ||
        !!permission?.all ||
        !!permission?.create ||
        !!permission?.delete);
  const isDisabledWithAppTypeCompanyPolicies =
    appType === APP_TYPES.COMPANY_POLICIES && !!pageModeParam;

  const [isEditing, setIsEditing] = useState(isEditMode);
  const [hasChanged, setHasChanged] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isStartPrinting, setIsStartPrinting] = useState(false);
  const contentPdfRef = useRef<HTMLDivElement>(null);

  const reactToPrint = useReactToPrint({
    contentRef: contentPdfRef,
    onAfterPrint: () => setIsStartPrinting(false),
    onPrintError: () => setIsStartPrinting(false),
  });

  const {
    data: dataApp,
    isLoading: isLoadingApp,
    isSuccess: isSuccessApp,
  } = useFetchOneAppByTypeV2(appType, companyId);

  const appParts = dataApp?.app_parts;

  const {
    data: recordDataRaw,
    isLoading: isLoadingRecord,
    isFetching: isFetchingRecord,
    isSuccess: isSuccessRecord,
  } = useFetchRecordV2({
    appType,
    appId,
    recordId: id,
    companyId: companyId,
    isDisabled: isDisabledWithAppTypeCompanyPolicies,
    workerId: currentWorker?.id,
    isAdmin: isAdmin || isSuperAdmin,
  });

  const appRecord = useMemo(() => {
    if (isDisabledWithAppTypeCompanyPolicies) {
      return dataContext || {};
    }
    return recordDataRaw?.data || {};
  }, [
    recordDataRaw,
    dataContext,
    isDisabledWithAppTypeCompanyPolicies,
  ]) as AppRecordV2;

  const { mutateAsync: updateRecordAsync, isLoading: isUpdating } =
    useUpdateRecordV2({ appType, appId, companyId });

  const isRegulationApp = useMemo(() => {
    return (
      dataApp?.name?.includes("規程") ||
      appParts?.some((p) => p.label_text === "規程名" || p.name === "規程名")
    );
  }, [dataApp, appParts]);

  const appPartsFlat = useMemo(
    () => appParts?.filter((x) => !!x.item_config_data) || [],
    [appParts],
  );

  const appPartsTree = useMemo(
    () => buildTreeAppPartType(appPartsFlat),
    [appPartsFlat],
  );

  const selectionDataList = useGetSelectOptionByAppPartType(
    appPartsTree,
    companyId,
  );

  const handleSubmit = async (values: any) => {
    if (appParts) {
      const dataSubmit = handleDataSubmitFinish(values, appParts, appType);

      await updateRecordAsync({ payload: dataSubmit, recordId: id });
      setHasChanged(false);
    }
  };

  const result = useMemo(() => {
    return convertDataToDataForm(appParts || [], appRecord);
  }, [appParts, appRecord]);

  useEffect(() => {
    form.resetFields();
  }, [id, form]);

  useEffect(() => {
    if (result) {
      form.resetFields();
      form.setFieldsValue(result);
      setHasChanged(false);
      setDataVersion((v) => v + 1);
    }
  }, [result, form]);

  useEffect(() => {
    setIsEditing(isEditMode);
  }, [isEditMode]);

  const waitForImages = (container: HTMLElement): Promise<void> => {
    const imgs = Array.from(
      container.querySelectorAll<HTMLImageElement>("img"),
    );
    if (!imgs.length) return Promise.resolve();
    return Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve(); // không block khi ảnh lỗi
          }),
      ),
    ).then(() => undefined);
  };

  useEffect(() => {
    if (!isStartPrinting || !contentPdfRef.current) return;

    const el = contentPdfRef.current;
    el.hidden = false;

    const wrapper = el.querySelector("#pdf-scale-wrapper") as HTMLElement;
    if (!wrapper) {
      reactToPrint();
      return;
    }

    if (!isRegulationApp) {
      wrapper.style.width = "max-content";
    }

    const contentWidth = wrapper.scrollWidth;
    const printableWidth = appType === APP_TYPES.AGREEMENTS ? 1600 : 1122; // A4
    const scale =
      isRegulationApp || appType === APP_TYPES.EMPLOYMENT_CONTRACTS
        ? 1
        : Math.min(1, (printableWidth / contentWidth) * 1);

    if (appType === APP_TYPES.FINANCIAL_REPORTS) {
      wrapper.style.transform = "none";
      (wrapper.style as any).zoom = scale;
      wrapper.style.width = "100%";
      el.style.width = "100%";
      el.style.height = "auto";
      el.style.overflow = "visible";
    } else if (scale !== 1) {
      wrapper.style.transform = `scale(${scale})`;
      wrapper.style.transformOrigin = "top left";
      wrapper.style.width = `${contentWidth}px`;

      const contentHeight = wrapper.scrollHeight;
      el.style.width = `${contentWidth * scale}px`;
      el.style.height = `${contentHeight * scale}px`;
      el.style.overflow =
        appType === APP_TYPES.LABOR_DOCUMENTS ? "visible" : "hidden";
    } else {
      wrapper.style.transform = "none";
      if (!isRegulationApp) {
        wrapper.style.width = "max-content";
        el.style.width = "max-content";
      }
      el.style.height = "auto";
      el.style.overflow = "visible";
    }

    // Chờ tất cả ảnh load xong rồi mới in
    waitForImages(wrapper).then(() => {
      if (scale !== 1 && appType !== APP_TYPES.FINANCIAL_REPORTS) {
        const newHeight = wrapper.scrollHeight;
        el.style.height = `${newHeight * scale}px`;
      }

      reactToPrint();

      setTimeout(() => {
        wrapper.style.transform = "";
        (wrapper.style as any).zoom = "";
        wrapper.style.width = "";
        el.style.width = "";
        el.style.height = "";
        el.style.overflow = "";
        el.hidden = true;
      }, 1000);
    });
  }, [isStartPrinting, isRegulationApp, reactToPrint]);

  const renderButtonActions = () => {
    if (!appIdParam && !idParam) return;
    if (isQuickView) return;

    return (
      <div className="d-flex gap-2 ml-3">
        {props.configShowBtnsOnHeader?.btnToCreate && (
          <ButtonToCreate
            appType={appType}
            appId={appId}
            permission={permission}
            isEditing={isEditing}
            appRecord={appRecord}
          />
        )}
        {props.configShowBtnsOnHeader?.btnDuplicate && (
          <ButtonDuplicate
            appType={appType}
            appId={appId}
            recordId={id}
            isEditing={isEditing}
            onLoadingChange={setIsDuplicating}
            permission={permission}
          />
        )}
        {props.configShowBtnsOnHeader?.btnSave && (
          <ButtonToSave
            form={form}
            isEditing={isEditing}
            permission={
              hasRecordPermission
                ? { edit: dataContext?.policy_permissions?.record_edit }
                : permission
            }
            isUploading={uploadStatus?.isUploading}
            hasChanged={hasChanged}
            setHasChanged={setHasChanged}
            dataVersion={dataVersion}
          />
        )}
        {props.configShowBtnsOnHeader?.saveAsNew && (
          <ButtonSaveAsNew
            appType={appType}
            appId={appId}
            form={form}
            appParts={appParts}
            isEditing={isEditing}
            isUpdating={isUpdating}
            onLoadingChange={setIsCreating}
            permission={permission}
          />
        )}
        {props.configShowBtnsOnHeader?.btnSubmit && pageMode === "edit" && (
          <ButtonToSubmit
            form={form}
            isEditing={isEditing}
            permission={
              hasRecordPermission
                ? { edit: dataContext?.policy_permissions?.record_edit }
                : permission
            }
            isUploading={uploadStatus?.isUploading}
            hasChanged={hasChanged}
            setHasChanged={setHasChanged}
            dataVersion={dataVersion}
          />
        )}
        {props.configShowBtnsOnHeader?.btnCancel && pageMode === "edit" && (
          <ButtonToCancel
            form={form}
            isEditing={isEditing}
            permission={
              hasRecordPermission
                ? { edit: dataContext?.policy_permissions?.record_edit }
                : permission
            }
            isUploading={uploadStatus?.isUploading}
            hasChanged={hasChanged}
            setHasChanged={setHasChanged}
            dataVersion={dataVersion}
            initialValues={result}
          />
        )}
        {pageModeParam ? (
          <></>
        ) : (
          props.configShowBtnsOnHeader?.btnPreview && (
            <Button
              label={isEditing ? "プレビュー" : "編集"}
              disabled={!permission?.edit}
              onClick={() => setIsEditing(!isEditing)}
            />
          )
        )}
        {typeof props.extendsButtonRight === "function"
          ? props.extendsButtonRight(appRecord, hasChanged, { isEditing })
          : props.extendsButtonRight}

        {props.configShowBtnsOnHeader?.btnPrintPdf && (
          <Button label="印刷・PDF" onClick={() => setIsStartPrinting(true)} />
        )}
      </div>
    );
  };

  const elementSegmentedControlPageMode = contentElementSegmentedControl && (
    <div
      style={{
        paddingBlock: "8px",
      }}
    >
      {elementSegmentedControl}
    </div>
  );

  return (
    <Spin
      spinning={
        isLoadingRecord ||
        isLoadingApp ||
        isUpdating ||
        isCreating ||
        isDuplicating ||
        isFetchingRecord
      }
    >
      <div
        style={{
          minHeight: 100,
          minWidth: 100,
        }}
      >
        {isSuccessRecord && isSuccessApp && (
          <>
            {elementSegmentedControlPageMode}
            <StylesButton text_scale={displaySize}>
              {renderButtonActions()}
            </StylesButton>
            <Form
              form={form}
              onFinish={handleSubmit}
              disabled={
                (hasRecordPermission
                  ? !dataContext?.policy_permissions?.record_edit
                  : !permission?.edit) ||
                !isEditing ||
                disabledInEditMode
              }
              onValuesChange={() => setHasChanged(true)}
            >
              <LayoutApp
                form={form}
                dataApp={dataApp}
                appRecord={appRecord}
                isEditPage={true}
                isEditing={isEditing}
                disabledInEditMode={disabledInEditMode || isUserResigned}
                allowSearchDate
                companyId={companyId}
                selectionDataList={selectionDataList}
                {...props}
                showPrintButton={showPrintButton}
                onPrintPDF={() => setIsStartPrinting(true)}
                isPrinting={isStartPrinting}
                onChange={() => {
                  setHasChanged(true);
                }}
              />
            </Form>
            {isStartPrinting && (
              <div ref={contentPdfRef} className="print-container">
                <div id="pdf-scale-wrapper">
                  {appType === APP_TYPES.BUSINESS_TRIP_REPORTS ? (
                    <BusinessTripReportPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                      excludeFieldNames={props.excludePdfFieldNames}
                    />
                  ) : isRegulationApp ? (
                    <RegulationPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                    />
                  ) : appType === APP_TYPES.AGREEMENTS ? (
                    <AgreementsPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                    />
                  ) : appType === APP_TYPES.EMPLOYMENT_CONTRACTS ? (
                    <EmploymentContractPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                    />
                  ) : appType === APP_TYPES.LABOR_DOCUMENTS ? (
                    <LaborDocumentPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                    />
                  ) : appType === APP_TYPES.FINANCIAL_REPORTS ? (
                    <FinancialReportsPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                    />
                  ) : (
                    <CommonPDF
                      dataApp={dataApp}
                      appRecord={appRecord}
                      selectionDataList={selectionDataList}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

const EditPage = (props: IProps) => {
  return (
    <UploadStatusProvider>
      <EditPageInner {...props} />
    </UploadStatusProvider>
  );
};

export default EditPage;
